import { Box, Divider, ITheme, Typography } from '@mui/material'
import React from 'react'
import SectionTitle from '../common/SectionTitle'
import FundSelectionList from '../fundSelection/FundSelectionList'
import FundFeesProvidedValues from '../fundFees/FundFeesProvidedValues'
import StageDistributionGraph from '../../common/StageDistributionGraph'
import GeographicDistributionGraph from '../../common/GeographicDistributionGraph'
import FundFeesCalculatedProjection from '../fundFees/FundFeesCalculatedProjection'
import { useAppSelector } from '../../../store/reducers/store'
import { FundItem } from '../../../models/Fund'
import { SectorAffectation } from '../../../models/Portfolio'
import { GeographicDistribution, StageDistribution } from '../../../models/Common'
import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { Utilities } from '../../../utility/utils'
import { ReactComponent as moneyIcon } from '../../../assets/donation.svg';
import { ReactComponent as dollarIcon } from '../../../assets/c_dollars.svg';
import { ReactComponent as slidersIcon } from '../../../assets/sliders.svg';
import { ReactComponent as camembertIcon } from '../../../assets/camembert.svg';
import { ReactComponent as worldIcon } from '../../../assets/world.svg';
import { ReactComponent as barsIcon } from '../../../assets/bars.svg';
import LeoTag from '../../common/LeoTag'

const FundSummaryContent: React.FC = () => {
    const theme = useTheme() as ITheme;
    const { t } = useTranslation();
    const portfolio = useAppSelector(state => state.createPortfolio.portfolio);
    const funds = useAppSelector(state => state.libraryOfFundsReducer.funds);

    if (portfolio === undefined || funds === undefined) {
        return <></>;
    }

    const selectedFunds = portfolio.funds.map(sf => {
        const f = funds.find(f => f.id === sf.id);
        if (f) {
            return { fund: f, proportion: sf.proportion };
        } else {
            return undefined;
        }
    }).filter(f => f !== undefined) as { fund: FundItem, proportion: number }[];

    // const allocation = selectedFunds.map(a => ({ name: a.fund.name, logo: a.fund.logo, amount: a.proportion * portfolio.fundraisingTarget[1] })) as PortfolioFundAllocation[];
    const sectors: SectorAffectation[] = [];
    const stage: StageDistribution = { early: 0, growth: 0, late: 0 };
    const geo: GeographicDistribution[] = [];
    for (const fund of selectedFunds) {
        for (const sector of fund.fund.sectorDistribution) {
            const index = sectors.findIndex(s => s.name === sector.sector);
            if (index === -1) {
                sectors.push({ icon: sector.icon, name: sector.sector, proportion: sector.proportion * fund.proportion, id: 0 });
            } else {
                sectors[index].proportion += sector.proportion * fund.proportion;
            }
        }
        for (const distrib of fund.fund.geographicDistribution) {
            const index = geo.findIndex(s => s.zone === distrib.zone);
            if (index === -1) {
                geo.push({ icon: distrib.icon, zone: distrib.zone, proportion: distrib.proportion * fund.proportion });
            } else {
                geo[index].proportion += distrib.proportion * fund.proportion;
            }
        }
        stage.early += fund.fund.stageDistribution.early * fund.proportion;
        stage.growth += fund.fund.stageDistribution.growth * fund.proportion;
        stage.late += fund.fund.stageDistribution.late * fund.proportion;
    }
    sectors.sort((a, b) => b.proportion - a.proportion)
    geo.sort((a, b) => b.proportion - a.proportion)

    let allocatedFunds = [...portfolio.funds];
    allocatedFunds = allocatedFunds.sort((a, b) => b.proportion - a.proportion)

    return <Box display={"flex"} flexDirection={"column"} width={"100%"} bgcolor={theme.palette.background.paper} borderRadius={"8px"} padding={"25px 28px"} gap={"19px"}>
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
            <Typography fontSize={"32px"} lineHeight={"39.01px"} fontWeight={"700"}>{portfolio.name}</Typography>
            <Box display={"flex"} flexDirection={"column"} alignItems={"flex-end"} justifyContent={"center"} gap={"8px"}>
                <Typography fontSize={"24px"} lineHeight={"29.26px"} fontWeight={"600"}>€{Utilities.toFriendlyMoneyValue(portfolio.fundraisingTarget)}</Typography>
                <Typography fontSize={"14px"} lineHeight={"10px"} fontWeight={"600"} color={theme.palette.leo.type[400]}>{t('PortfolioPageItemFundraisingTarget')}</Typography>
            </Box>
        </Box>
        <Divider sx={{ margin: "0 -28px" }} />
        <Box display={"flex"} gap={"133px"}>
            <Box display={"flex"} flexDirection={"column"} flexGrow={1} flexBasis={"45%"} gap={"17px"}>
                <SectionTitle title={`${t('CreatePortfolioFundsSelected')} (${portfolio.funds.length})`} icon={moneyIcon} />
                <Divider />
                <FundSelectionList funds={allocatedFunds} hideRemove portfolioSize={portfolio.fundraisingTarget} />
                <SectionTitle title={t('CreatePortfolioFees')} icon={dollarIcon} />
                <Divider />
                <FundFeesProvidedValues />
            </Box>
            <Box display={"flex"} flexDirection={"column"} flexGrow={1} flexBasis={"45%"} gap={"19px"}>
                <SectionTitle title={t('PortfolioPageItemSectorDistribution')} icon={slidersIcon} />
                <Divider />
                <Box display={"flex"} gap={"12px"} flexWrap={"wrap"}>
                    {sectors.map(s => <LeoTag key={`sector-${s.name}`} text={s.name} value={s.proportion} icon={s.icon} size='medium' />)}
                </Box>
                <SectionTitle title={t('PortfolioPageItemStageDistribution')} icon={camembertIcon} />
                <Divider />
                <StageDistributionGraph data={stage} />
                <SectionTitle title={t('PortfolioPageItemGeographicDistribution')} icon={worldIcon} />
                <Divider />
                <GeographicDistributionGraph distribution={geo} />
                <SectionTitle title={t('CreatePortfolioProjection')} icon={barsIcon} />
                <Divider />
                <FundFeesCalculatedProjection />
            </Box>
        </Box>
    </Box>
}

export default FundSummaryContent